import type { SxStyle } from '@aibees/shared/theme';
import {
  BaseSelectProps,
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  type TextFieldProps,
} from '@mui/material';
import React, { useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Icon, IconName } from '../icon';

export type RHFSelectProps = TextFieldProps & {
  name: string;
  label?: string;
  native?: boolean;
  icon?: IconName;
  iconSx?: SxStyle;
  textFieldSx?: SxStyle;
  maxHeight?: boolean | number;
  children: React.ReactNode;
  PaperPropsSx?: SxStyle;
  defaultValue?: string;
  textTransform?: React.CSSProperties['textTransform'];
};

export function RHFSelect({
  name,
  native,
  icon,
  label,
  iconSx,
  textFieldSx,
  maxHeight = 220,
  helperText,
  children,
  PaperPropsSx,
  defaultValue,
  placeholder,
  textTransform,
  ...other
}: RHFSelectProps) {
  const { control } = useFormContext();
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field, fieldState: { error } }) => (
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          {icon && (
            <Stack
              justifyContent="center"
              alignItems="center"
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                pl: 1,
                zIndex: 1,
                maxHeight: inputRef.current?.clientHeight ?? 'unset',
              }}
            >
              <Icon name={icon} sxx={iconSx} color="grey.600" size={20} />
            </Stack>
          )}
          {label && <InputLabel sx={{ pl: icon ? 3 : 0 }}>{label}</InputLabel>}

          <TextField
            {...field}
            select
            fullWidth
            autoComplete="off"
            SelectProps={{
              native,
              MenuProps: {
                PaperProps: {
                  sx: {
                    ...(!native && {
                      maxHeight:
                        typeof maxHeight === 'number' ? maxHeight : 'unset',
                    }),
                    ...PaperPropsSx,
                  },
                },
              },
              size: other.size,
              slotProps: { input: { sx: { p: 0 } }, root: { sx: { p: 0 } } },
              sx: {
                [`& > .MuiSelect-select`]: {
                  display: 'flex',
                  flexDirection: 'row',
                },
                ...{
                  // pl: 2,
                  ...(icon && {
                    '.MuiInputBase-input': {
                      // pl: 2,
                    },
                  }),
                  textTransform: textTransform || 'capitalize',
                  display: 'flex',
                },
                ...textFieldSx,
              },
              displayEmpty: !!placeholder,
              ref: inputRef,
            }}
            error={!!error}
            helperText={error ? error?.message : helperText}
            {...other}
          >
            {!!placeholder && (
              <MenuItem key="placeholder" value={undefined} disabled>
                {placeholder}
              </MenuItem>
            )}
            {children}
          </TextField>
        </Box>
      )}
    />
  );
}

type RHFMultiSelectProps = BaseSelectProps<string[]> & {
  name: string;
  label?: string;
  icon?: IconName;
  iconSx?: SxStyle;
  chip?: boolean;
  checkbox?: boolean;
  placeholder?: string;
  helperText?: React.ReactNode;
  options: {
    label: string;
    value: string;
  }[];
};

export function RHFMultiSelect({
  name,
  chip,
  label,
  icon,
  iconSx,
  options,
  checkbox,
  placeholder,
  helperText,
  sx,
  ...other
}: RHFMultiSelectProps) {
  const { control } = useFormContext();

  const renderValues = (selectedIds: string[]) => {
    const selectedItems = options.filter((item) =>
      selectedIds.includes(item.value)
    );

    if (!selectedItems.length && placeholder) {
      return (
        <Box component="em" sx={{ color: 'text.disabled' }}>
          {placeholder}
        </Box>
      );
    }

    if (chip) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selectedItems.map((item) => (
            <Chip key={item.value} size="small" label={item.label} />
          ))}
        </Box>
      );
    }

    return selectedItems.map((item) => item.label).join(', ');
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl sx={sx}>
          <Box sx={{ position: 'relative' }}>
            {icon && (
              <Stack
                justifyContent="center"
                alignItems="center"
                sx={{ position: 'absolute', top: 0, bottom: 0, pl: 1 }}
              >
                <Icon name={icon} sxx={iconSx} />
              </Stack>
            )}
            {label && <InputLabel id={name}> {label} </InputLabel>}

            <Select
              {...field}
              multiple
              displayEmpty={!!placeholder}
              labelId={name}
              input={<OutlinedInput fullWidth label={label} error={!!error} />}
              renderValue={renderValues}
              {...other}
            >
              {placeholder && (
                <MenuItem disabled value="">
                  <em> {placeholder} </em>
                </MenuItem>
              )}

              {options.map((option) => {
                const selected = field?.value?.includes(option.value);

                return (
                  <MenuItem key={option.value} value={option.value}>
                    {checkbox && (
                      <Checkbox size="small" disableRipple checked={selected} />
                    )}

                    {option.label}
                  </MenuItem>
                );
              })}
            </Select>

            {(!!error || helperText) && (
              <FormHelperText error={!!error}>
                {error ? error?.message : helperText}
              </FormHelperText>
            )}
          </Box>
        </FormControl>
      )}
    />
  );
}
